/**
 * 서비스 필터링 Hook
 */
import { useState, useCallback, useMemo } from 'react';
import { MSAService } from '@/types';

interface UseServiceFiltersProps {
  services: MSAService[];
  activeTab: string;
}

export const useServiceFilters = ({ services, activeTab }: UseServiceFiltersProps) => {
  const [searchText, setSearchText] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterVisibility, setFilterVisibility] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  // 검색 및 필터링된 서비스 목록
  const getFilteredServices = useCallback((tab: string) => {
    let filtered = services;

    // 탭별 기본 필터링
    if (tab === 'all') {
      filtered = services.filter(s => s.status !== 'deleted' && s.status !== 'rejected');
    } else if (tab === 'deleted_rejected') {
      filtered = services.filter(s => s.status === 'deleted' || s.status === 'rejected');
    } else if (tab === 'approved') {
      // 등록된 서비스 탭: approved와 suspended 모두 포함
      filtered = services.filter(s => s.status === 'approved' || s.status === 'suspended');
    } else {
      filtered = services.filter(s => s.status === tab);
    }
    
    // 검색어 필터링 (서비스명, 설명, 개발사)
    if (searchText.trim()) {
      const query = searchText.toLowerCase();
      filtered = filtered.filter(service =>
        service.service_name.toLowerCase().includes(query) ||
        service.service_description?.toLowerCase().includes(query) ||
        service.contact_name?.toLowerCase().includes(query)
      );
    }
    
    // 카테고리 필터링
    if (filterCategory !== 'all') {
      filtered = filtered.filter(service => service.category === filterCategory);
    }
    
    // 공개/비공개 필터링
    if (filterVisibility !== 'all') {
      const isPublic = filterVisibility === 'public';
      filtered = filtered.filter(service => service.is_public === isPublic);
    }
    
    // 상태 필터링 (탭과 별도로 추가 필터링)
    if (filterStatus !== 'all') {
      filtered = filtered.filter(service => service.status === filterStatus);
    }
    
    return filtered;
  }, [services, searchText, filterCategory, filterVisibility, filterStatus]);

  const filteredServices = useMemo(() => {
    return getFilteredServices(activeTab);
  }, [activeTab, getFilteredServices]);

  const resetFilters = useCallback(() => {
    setSearchText('');
    setFilterCategory('all');
    setFilterVisibility('all');
    setFilterStatus('all');
  }, []);

  return {
    // State
    searchText,
    filterCategory,
    filterVisibility,
    filterStatus,
    filteredServices,
    // Actions
    setSearchText,
    setFilterCategory,
    setFilterVisibility,
    setFilterStatus,
    resetFilters,
    getFilteredServices,
  };
};
